/**
 * Zod schemas for the Contact State Resolver — Read API v1
 * (plans/2026-07-22-001-contact-state-resolver-plan.md).
 *
 * Canonical shapes for the cross-Terra contact-state resolver: a source-adapter
 * registry that projects disparate data (SFDC mirror, launcher tables, later
 * the enroll queue / Signal_Action mirror) into one normalized StateFact /
 * ContactStatePill shape, exposed through a list read (pills only, set-based)
 * and a detail read (pills + facts). This file is the contract every adapter
 * and both HTTP endpoints validate against.
 *
 * Two enums, different lifecycles (plan Decision 2):
 * - StateDimensionSchema is the wire contract. It deliberately includes
 *   dimensions with no adapter yet ('queued', 'open_task', 'gifted') so
 *   consumers/UI never see the enum change as adapters register.
 * - ContactStateSourceKeySchema is the registry key. It grows one value per
 *   adapter — adding a key is a code change (schema value + adapter in the
 *   same commit), never pre-registered ahead of the adapter landing. v1
 *   shipped 'gong_flow' and 'launcher_invites'; 'signal_actions' (the
 *   `open_task` / Live Task adapter over the salesforce_signal_action
 *   mirror, PR #1084) is the third.
 *
 * evidence_ref (plan Decision 5): a stable reference string, NOT a url.
 * Format is "<scheme>:<id>", e.g. `sfdc:contact:003...`,
 * `launcher:campaign:<campaign_id>`. URL construction (linking into SFDC,
 * Playhouse, Action View, etc.) is the consumer's job — the resolver and its
 * adapters must never hardcode an instance URL or app path.
 *
 * Types via z.infer only.
 */

import { z } from 'zod';
import { ContactRoleSchema } from '../product-usage/wire';
import {
  LIVE_TASK_LABEL,
  GONG_TASK_LABEL,
  UPCOMING_CAMPAIGN_LABEL,
  RECENT_CAMPAIGN_LABEL,
  SUGGESTED_INVITE_LABEL,
  RECENT_TASK_LABEL,
} from '../flow/pill-variants';

// ─────────────────────────────────────────────────────────────
// Enums
// ─────────────────────────────────────────────────────────────

/**
 * Wire-level state dimensions. Only 'gifted' has no registered adapter yet
 * (it registers when a gift play exists) — included here so the dimension
 * enum is stable across that future addition. The other seven are live:
 * in_flow (gong_flow), invited (launcher_invites), open_task
 * (signal_actions), queued (enrollment_queue), recent_request
 * (recent_request — flow-workflow-requests U8, over ActionExecution),
 * warm_intro (warm_intro — warm-intro plan U5/B1, over the
 * `salesforce_warm_intro` mirror; ONE pill per connector, so this dimension
 * routinely carries several pills for one contact via the `pillLabel`
 * variant hook), recent_queue_send (recent_queue_send — 2026-09-01
 * flow-contacts-bulk-send plan U4, over `enrollment_items` — "was this
 * contact sent ANY play from the queue in the last 30 days?", cross-owner,
 * flat/no-tiers. Fully independent of `invited`: no suppression in either
 * direction, R9).
 */
export const StateDimensionSchema = z.enum([
  'in_flow',
  'invited',
  'queued',
  'open_task',
  'gifted',
  'recent_request',
  'warm_intro',
  'recent_queue_send',
]);
export type StateDimension = z.infer<typeof StateDimensionSchema>;

/**
 * Registry keys with a registered adapter in
 * src/services/flow/contact-state.ts. Unlike StateDimensionSchema, this enum
 * only ever holds keys with a live adapter — adding a key is a code change
 * (schema value + adapter), never a pre-registration ahead of the adapter.
 *
 * 'enrollment_queue' (the `queued` dimension's adapter, over `enrollment_items`)
 * is the fourth. 'recent_request' (flow-workflow-requests U8, Key Technical
 * Decision #5) is the fifth — a distinct dimension (`recent_request`), not a
 * variant of an existing one: it has its own semantics (per-run provenance,
 * a possible staged output-binding value awaiting sync) and its own evidence
 * intent (`flow:request` → `requests-open`, state-links.ts). See
 * src/services/flow/contact-state-sources.ts's `recentRequestSource`.
 *
 * 'warm_intro' (warm-intro plan U5/B1, requirement R3) is the sixth — one
 * `Warm Intro: {Connector Name}` pill per `salesforce_warm_intro` mirror
 * row, suppressed per-connector while an ACTIVE warm-intro queue item
 * already carries that connector (R8). Its evidence intent is
 * `flow:warm-intro:<warmIntroId>` (state-links.ts, U6). See
 * `warmIntroSource`.
 *
 * 'recent_queue_send' (2026-09-01 flow-contacts-bulk-send plan U4,
 * requirements R9/R10) is the seventh — one fact per SUBMITTED
 * `enrollment_items` row with a play assigned, inside the shared 30-day
 * `recent-sends.ts` window (`queryRecentQueueSendRows`, plan U1). Flat: no
 * tiers, no `campaignBucket`, no suppression against `invited` in either
 * direction — the two dimensions are fully independent (R9). Its evidence
 * ref reuses the REGISTERED `flow:request:<execution_id>` scheme when the
 * row carries one, otherwise emits no fact-level link. See
 * `recentQueueSendSource`.
 */
export const ContactStateSourceKeySchema = z.enum([
  'gong_flow',
  'launcher_invites',
  'signal_actions',
  'enrollment_queue',
  'recent_request',
  'warm_intro',
  'recent_queue_send',
]);
export type ContactStateSourceKey = z.infer<typeof ContactStateSourceKeySchema>;

/**
 * How current a fact/pill is:
 * - durable: a mirror column with no reliable per-row currency timestamp
 *   (e.g. the SFDC Gong mirror — see prisma/schema.prisma:1640-1648).
 * - observed: derived from an event row with its own timestamp (e.g. an
 *   invite action) that is current by definition as of that event.
 * - live: read directly from a live system at request time (no v1 adapter
 *   is 'live'; reserved for a future proxy-backed source).
 */
export const FreshnessSchema = z.enum(['durable', 'observed', 'live']);
export type Freshness = z.infer<typeof FreshnessSchema>;

// ─────────────────────────────────────────────────────────────
// Contact header (identity — not a state)
// ─────────────────────────────────────────────────────────────

/**
 * Contact identity + display fields, loaded once per batch (plan Decision 4)
 * and handed to every adapter. committeeRole is a Keystone buying-committee
 * attribute — a ranking/join tag on the contact, not a StateFact — and is
 * contract-only in v1: always null until the Keystone identity-key question
 * resolves.
 *
 * usageRoles is a Product Usage attribute (Champion / Key builder / Builder,
 * from `account_workspace_user` plus the won-opportunity `champion_id`). Like
 * committeeRole it is an attribute on the header, not a StateFact: it never
 * becomes a pill and never affects ranking. Most significant first; [] when
 * the contact holds no role or when the caller opted out of the lookup
 * (ContactStateResolveContext.includeUsageRoles: false).
 */
export const ContactHeaderSchema = z.object({
  contactId: z.string(),
  name: z.string(),
  email: z.string(),
  accountId: z.string().nullable(),
  accountName: z.string().nullable(),
  ownerName: z.string().nullable(),
  title: z.string().nullable(),
  committeeRole: z.string().nullable(),
  usageRoles: z.array(ContactRoleSchema),
});
export type ContactHeader = z.infer<typeof ContactHeaderSchema>;

// ─────────────────────────────────────────────────────────────
// Pills (list read — set-based aggregate, no raw facts)
// ─────────────────────────────────────────────────────────────

/**
 * One dimension's aggregate state for a contact. The list read
 * (POST /api/flow/contacts/state) returns pills only — never raw facts — so
 * it stays cheap at 500-row page scale. latest_at/as_of are nullable to cover
 * durable sources with no per-row currency (see FreshnessSchema).
 */
export const ContactStatePillSchema = z.object({
  dimension: StateDimensionSchema,
  present: z.boolean(),
  count: z.number().int().nonnegative(),
  label: z.string(),
  // ISO-8601 datetime, offset permitted (facts can carry non-UTC timestamps —
  // see StateFactSchema.observed_at). Mirrors src/shared/schemas/
  // terra-ingestion.ts:217,260, widened with { offset: true }.
  latest_at: z.string().datetime({ offset: true }).nullable(),
  freshness: FreshnessSchema,
  as_of: z.string().datetime({ offset: true }).nullable(),
  // Inherited admin-PriorityConfig rank (ranking consistency, 2026-07-30):
  // the MIN rank of the facts collapsed into this pill (aggregatePills,
  // contact-state.ts). Only `open_task` pills set it today (their facts carry
  // a per-SA rank from signalActionsSource); every other dimension omits it.
  // Lower = higher priority. It is a WITHIN-dimension-band tiebreak in
  // rankTodos — NEVER a top-level cross-dimension key (PILL_DIMENSION_PRIORITY
  // stays primary). Optional/nullable so pills without a rank are unchanged.
  priorityRank: z.number().nullable().optional(),
});
export type ContactStatePill = z.infer<typeof ContactStatePillSchema>;

// ─────────────────────────────────────────────────────────────
// Facts (detail read — one contact, full evidence)
// ─────────────────────────────────────────────────────────────

/**
 * One raw observation from one adapter. detail is an open, adapter-defined
 * payload (z.unknown()) — each adapter documents its own detail shape
 * alongside its resolve() implementation; this contract only guarantees the
 * envelope. evidence_ref is a stable "<scheme>:<id>" reference string, not a
 * URL (see file header).
 */
export const StateFactSchema = z.object({
  contact_key: z.string(),
  dimension: StateDimensionSchema,
  status: z.string(),
  source: ContactStateSourceKeySchema,
  // ISO-8601 datetime, offset permitted — facts can legitimately carry a
  // non-UTC-offset timestamp (an observed event stamped in local time). UTC-only
  // .datetime() would wrongly reject those. Mirrors terra-ingestion.ts:217,260.
  observed_at: z.string().datetime({ offset: true }),
  freshness: FreshnessSchema,
  as_of: z.string().datetime({ offset: true }).nullable(),
  evidence_ref: z.string(),
  detail: z.unknown().optional(),
  // Resolver-internal admin-PriorityConfig rank for this fact (ranking
  // consistency, 2026-07-30). Set by signalActionsSource when a ranking is
  // threaded via ctx (open_task facts only); aggregatePills reduces the facts'
  // ranks to the pill's `priorityRank` (MIN). Optional/nullable — a fact with
  // no ranking context omits it and is byte-identical to pre-feature output.
  priorityRank: z.number().nullable().optional(),
});
export type StateFact = z.infer<typeof StateFactSchema>;

// ─────────────────────────────────────────────────────────────
// Read-shape envelopes
// ─────────────────────────────────────────────────────────────

/** One row of the list read: header + pills only (no facts). */
export const ContactStateListRowSchema = z.object({
  contact: ContactHeaderSchema,
  pills: z.array(ContactStatePillSchema),
});
export type ContactStateListRow = z.infer<typeof ContactStateListRowSchema>;

// 500 matches the resolver's stated page size (plan Decision 6) — the list
// read is set-based per adapter, never per-contact fan-out.
/** Request body of `POST /api/flow/contacts/state`. */
export const ContactStateListRequestSchema = z.object({
  contactIds: z.array(z.string().min(1)).min(1).max(500),
});

/** Wire body of `POST /api/flow/contacts/state`. */
export const ContactStateListResponseSchema = z.array(ContactStateListRowSchema);
export type ContactStateListResponse = z.infer<typeof ContactStateListResponseSchema>;

/** The detail read for one contact: header + pills + the raw facts behind them. */
export const ContactStateDetailSchema = z.object({
  contact: ContactHeaderSchema,
  pills: z.array(ContactStatePillSchema),
  facts: z.array(StateFactSchema),
});
export type ContactStateDetail = z.infer<typeof ContactStateDetailSchema>;

// ─────────────────────────────────────────────────────────────
// Pill dedup + priority (Spencer, 2026-07-27) — resolver-internal, NOT on
// the wire. ContactStatePillSchema is unchanged; this only orders the
// `pills` array the resolver assembles before returning it.
// ─────────────────────────────────────────────────────────────

/**
 * Total pill-priority order (contact-state pill dedup + priority, Spencer
 * 2026-07-27 design, ranking refinement same day; reordered Queued-first per
 * Spencer's 2026-07-27 directive — "the pill priority should be changed at
 * the source and then the sidebar and the pill rank inline for contacts
 * should inherit it", flow-sidebar-redesign-todos plan U1): the resolver
 * (src/services/flow/contact-state.ts resolveContactStateList /
 * resolveContactStateDetail) sorts a contact's assembled pills into this
 * order before returning them, regardless of CONTACT_STATE_SOURCES'
 * registry-insertion order or a multi-row adapter's arbitrary DB row order.
 * This is a SHARED ranking — both the contact-card pill strips and the Flow
 * sidebar's ranked Todos list inherit it; there is no todos-local override.
 *
 * Entries are (dimension, label) pairs. `label: null` is a WILDCARD that
 * matches any pill of that dimension whose label doesn't match a MORE
 * specific entry for the same dimension listed elsewhere in this array (see
 * `pillPriorityRank`). Three dimensions carry a pill-variant split
 * (aggregatePills' `pillLabel` mechanism, contact-state.ts) and get multiple
 * entries each, ranked independently and NOT (necessarily) adjacently:
 *   - `queued` ranks FIRST, overall (Queued-first directive, 2026-07-27) —
 *     a queued play is the most actionable next step Terra itself already
 *     knows about, so it now outranks even a direct open task.
 *   - `open_task` — THREE strata (ranking refinement, 2026-07-27): 'Live
 *     Task' (open, no gong linkage) is the highest-priority open_task strata
 *     (most urgent of the three — a direct open task needs action now);
 *     'Gong Task' (open, gong-linked — signalActionsSource's
 *     `isGongFlowTask`) ranks #3a, immediately BEFORE `in_flow`/'Gong Flow'
 *     (#3b) — a concrete, SA-backed gong task outranks the flag-only Gong
 *     Flow pill that carries no task of its own; 'Recent Task' (closed,
 *     regardless of linkage) ranks LAST — a closed task is resolved, the
 *     lowest-urgency signal on the strip.
 *   - `invited`: real campaign memberships (tiers 1/2 — campaign_member /
 *     invite_sent) outrank 'Suggested Invite' (tier 3) — an actual campaign
 *     membership is a stronger signal than a mere suggestion. Within the two
 *     membership labels, 'Upcoming Campaign' (start_date in the future)
 *     outranks 'Recent Campaign' (start_date in the past) — an upcoming event
 *     is more actionable (prep/confirm now) than one that already happened
 *     (Spencer, 2026-07-31).
 * `in_flow`/`queued`/`gifted` have exactly one pill label each in practice
 * today (see DIMENSION_STYLES' `label` defaults in StatePills.tsx), so a
 * single wildcard entry covers each.
 *
 * Read top-to-bottom, the full order is: Queued (rank 1) > a DIRECT live
 * task (rank 2) > a GONG-DRIVEN live task (rank 3a) > the flag-only Gong
 * Flow pill (rank 3b, no SA of its own — see gongFlowSource's suppression,
 * which normally means 'Gong Flow' and 'Gong Task' never co-occur for the
 * same contact, but the table stays total regardless) > Recent Queue Send >
 * Upcoming Campaign > Recent Campaign > Suggested Invite > Gift > a closed
 * task (rank last, any linkage).
 *
 * `recent_queue_send` (2026-09-01 flow-contacts-bulk-send plan U4, Decision
 * 12) is a WILDCARD entry placed after `in_flow` and before every `invited`
 * variant — a red "do-not-touch-yet" signal should outrank mere campaign
 * membership. REVIEW-THESE default (Spencer QA, per the plan) — this
 * placement is not yet confirmed. It carries exactly one pill label in
 * practice today (RECENT_QUEUE_SEND_LABEL, contact-state-sources.ts), so a
 * single wildcard entry covers it, same as `in_flow`/`queued`/`gifted` below.
 *
 * `recent_request` (flow-workflow-requests, PR #1122) is intentionally NOT a
 * member of this table — see the trailing comment on the constant below.
 */
export const PILL_DIMENSION_PRIORITY: ReadonlyArray<{ dimension: StateDimension; label: string | null }> = [
  { dimension: 'queued', label: null },
  { dimension: 'open_task', label: LIVE_TASK_LABEL },
  { dimension: 'open_task', label: GONG_TASK_LABEL },
  { dimension: 'in_flow', label: null },
  { dimension: 'recent_queue_send', label: null },
  { dimension: 'invited', label: UPCOMING_CAMPAIGN_LABEL },
  { dimension: 'invited', label: RECENT_CAMPAIGN_LABEL },
  { dimension: 'invited', label: SUGGESTED_INVITE_LABEL },
  // warm_intro (warm-intro plan U5/B1) — a WILDCARD entry: every warm-intro
  // pill's label embeds its own connector name ('Warm Intro: Solange Levy'),
  // so per-label entries are impossible (the label set is the connector
  // roster, ~65 names and growing) and all warm-intro pills share one rank.
  // Placement (Spencer-approved REVIEW-THESE default): below every invite
  // variant — a real campaign membership or even a mere suggestion is a
  // touch already in motion — and above `gifted`. A warm intro is a strong
  // but not-yet-started path, so it sits at the quiet end of the strip.
  { dimension: 'warm_intro', label: null },
  { dimension: 'gifted', label: null },
  { dimension: 'open_task', label: RECENT_TASK_LABEL },
];
// `recent_request` (flow-workflow-requests, PR #1122) is a deliberate
// omission from this table, NOT an oversight: those pills are user-triggered
// action logs rendered in their own "Recent Actions" section (StatePills.tsx
// :80, ContactDetailPanels.tsx:171), never the ranked strip, so they never
// reach pillPriorityRank in practice — see the priority test's SOURCES
// fixture comment (contact-state-pill-priority.test.ts:173-179) for the
// full rationale.

// ─────────────────────────────────────────────────────────────
// Terra Actions filter (custom-views v0.1, Spencer 2026-08-12) — the
// stable DIMENSION-level labels the `flow_contact_state_pill_v` SQL view
// (prisma/schema/migrations/20260812120000_flow_contact_state_pill_view)
// emits as `pill_type`, and the single TS-side source of truth for the
// `state_pill_type` / `contact_state_pill_type` filter fields' `values` list
// (src/shared/flow/filter-fields.ts). Type granularity = DIMENSION, not the
// (dimension, label) strata PILL_DIMENSION_PRIORITY carries — a rep filtering
// "pill type is Task" means any open_task pill (Live/Gong/Recent Task), not
// one specific variant. `warm_intro`'s per-connector interpolation
// ('Warm Intro: {Connector}') collapses to the bare dimension label here for
// the same reason. `gifted` is deliberately excluded — no adapter/source
// table exists for it (see the enum doc comment above), so a filter value for
// it would always match zero rows.
export const PILL_TYPE_FILTER_VALUES = [
  'Queued',
  'Task',
  'Gong Flow',
  'Campaign / Invite',
  'Warm Intro',
  'Recent Request',
] as const;

/**
 * Rank a pill against PILL_DIMENSION_PRIORITY, lower = higher priority (sorts
 * first). An exact (dimension, label) match wins outright. Otherwise, the
 * FIRST wildcard (label: null) entry for that dimension wins. A pill whose
 * dimension has ONLY specific (non-wildcard) entries and none of them match
 * its label — a future pill variant this table hasn't been updated for —
 * degrades to sorting LAST (Infinity) rather than throwing; it still renders,
 * just at the tail of the strip.
 */
export function pillPriorityRank(pill: { dimension: StateDimension; label: string }): number {
  let wildcardRank = -1;
  for (let i = 0; i < PILL_DIMENSION_PRIORITY.length; i += 1) {
    const entry = PILL_DIMENSION_PRIORITY[i];
    if (entry.dimension !== pill.dimension) continue;
    if (entry.label === pill.label) return i;
    if (entry.label === null && wildcardRank === -1) wildcardRank = i;
  }
  return wildcardRank === -1 ? Number.POSITIVE_INFINITY : wildcardRank;
}

// ─────────────────────────────────────────────────────────────
// Todo wire contract + ranker (flow-sidebar-redesign-todos plan U2,
// Key Technical Decisions 4 & 5). A todo is a ranked projection of a single
// ContactStatePill — no new table, no new wire family, computed at read time.
// Ranking semantics SoT: Notion "Ranking for Contact State + Sidebar"
// (3aa7e66eb01480f7b137d05c954bb4c9). No repo copy by design.
// ─────────────────────────────────────────────────────────────

/**
 * Where a todo drills to (Terra-first destination policy, decision 5),
 * server-computed from the pill's dimension so an MCP/UI consumer never
 * re-derives destination policy:
 *   - `action_view` — SA-backed open-task pills (Live/Gong/Recent Task, and
 *     any future open_task label) drill into Action View.
 *   - `queue` — queued plays deep-link into the Plays workbench.
 *   - `gong` — the flag-only Gong Flow (`in_flow`) pill links out to Gong.
 *   - `launcher` — invite pills (`invited`) link into Playhouse/launcher.
 * `gifted` and `recent_request` intentionally have NO dest — they are excluded
 * from todos entirely (see `rankTodos`), so the enum is not extended for them.
 */
export const TodoDestSchema = z.enum(['action_view', 'queue', 'gong', 'launcher']);
export type TodoDest = z.infer<typeof TodoDestSchema>;

/**
 * Destinations temporarily EXCLUDED from the ranked todo list (Spencer,
 * 2026-07-28) — external-completion categories Terra can't honestly track to
 * done: `gong` (flag-only Gong Flow, completed in Gong) and `launcher`
 * (campaign-member invite, completed in Playhouse). The dest enum keeps both
 * (the resolver still computes them; a future read/unread treatment will
 * re-surface them), but `rankTodos` drops them for now. See
 * project_sidebar_redesign memory for the read/unread follow-up.
 */
const HIDDEN_TODO_DESTS: ReadonlySet<TodoDest> = new Set(['gong', 'launcher']);

/**
 * One ranked cross-contact todo (Next Best Action). Self-describing enough for
 * an MCP consumer to narrate and cite: it carries the full contact header, the
 * source pill, the server-computed `dest`, and a 1-based `rank` = the todo's
 * ordinal position in the ranked list. `evidence_ref` is nullable and is null
 * on the list path (pills carry no evidence_ref — the URL resolves client-side
 * from the detail read's facts via resolveEvidenceLink; decision 5).
 */
export const TodoSchema = z.object({
  contact: ContactHeaderSchema,
  pill: ContactStatePillSchema,
  rank: z.number(),
  evidence_ref: z.string().nullable(),
  dest: TodoDestSchema,
});
export type Todo = z.infer<typeof TodoSchema>;

/**
 * Wire envelope for `GET /api/flow/todos` (plan U3/U7). `total` is the full
 * ranked count BEFORE the endpoint's `limit` slice, so a client can tell "you
 * have N todos" apart from "here are the first `limit` of them". This is the
 * schema the frontend validates the response through at the trust boundary
 * (`useMyTodos`), and it mirrors the service-layer `MyTodosResult` shape
 * (src/services/flow/todos.ts) — Todo itself is already schema-derived on both
 * sides, so the envelope is the only thing that needed pinning here.
 */
export const MyTodosResponseSchema = z.object({
  todos: z.array(TodoSchema),
  total: z.number().int().nonnegative(),
});
export type MyTodosResponse = z.infer<typeof MyTodosResponseSchema>;

/**
 * Terra-first destination for a pill, or null if the pill is NOT a todo
 * (`gifted` — enum value with no adapter/product surface; `recent_request` —
 * user-triggered action logs rendered in their own "Recent Actions" section,
 * not next-actions; `warm_intro` — see below; `recent_queue_send` — see
 * below). Returning null is how `rankTodos` filters those out without
 * extending TodoDestSchema. Exhaustive over StateDimension.
 *
 * `warm_intro` (warm-intro plan U5/B1) returns null deliberately: a warm-intro
 * pill is AMBIENT VISIBILITY (this contact has up to ~3 people who could
 * intro us), not an assigned next action, and every eligible contact carries
 * one — ~1.12 rows across 5,480 contacts, so admitting them would flood every
 * rep's ranked Todos list with thousands of never-completing entries. Whether
 * warm intros should ever surface as todos is a product question outside this
 * plan; if it is answered yes later, the dest is `queue` (the pill's evidence
 * link is the add-to-queue intent).
 *
 * `recent_queue_send` (2026-09-01 flow-contacts-bulk-send plan U4) also
 * returns null: it is an ambient "watch out, we already touched this
 * contact" flag, not an assigned next action — there is nothing for a rep to
 * DO about it (mirrors `recent_request`'s "Recent Actions" framing, not an
 * actionable todo). Not scoped by this plan; revisit if product wants it
 * surfaced as a todo later.
 */
function todoDestForDimension(dimension: StateDimension): TodoDest | null {
  switch (dimension) {
    case 'open_task':
      return 'action_view';
    case 'queued':
      return 'queue';
    case 'in_flow':
      return 'gong';
    case 'invited':
      return 'launcher';
    case 'gifted':
    case 'recent_request':
    case 'warm_intro':
    case 'recent_queue_send':
      return null;
  }
}

/**
 * latest_at tiebreak between two pills that share a PILL_DIMENSION_PRIORITY
 * rank AND the same inherited priorityRank (decision 4, recency direction
 * unified 2026-07-30). Direction is now DESC (most recent signal first) for
 * EVERY dimension — including open_task, which previously sorted ASC
 * (oldest-first "most overdue"). The global ranking decision (2026-07-30) is
 * "rank is primary; recency is a newest-first tiebreak within the same rank",
 * matching Action View's usePrioritizedActions surfacedDate-DESC tiebreak so
 * the sidebar Todos, the contact-card, and Action View all agree.
 *
 * `in_flow` is the one exception: its latest_at is synthetic (see inline
 * comment) and never tiebreaks. A null/unparseable latest_at on either side
 * yields 0 so the caller falls back to stable insertion order — never throws.
 */
function compareLatestAt(a: ContactStatePill, b: ContactStatePill): number {
  // `in_flow` never tiebreaks on time: gongFlowSource stamps a SYNTHETIC
  // per-resolve `readAt` as latest_at ("true as of this read" — no recency
  // meaning), so comparing it would order contacts by which resolver CHUNK
  // they landed in (resolveMyTodos chunks books >500), not by any real
  // signal. Fall through to stable insertion order instead.
  //
  // Currently unreachable via rankTodos (in_flow → `gong` dest is hidden, see
  // HIDDEN_TODO_DESTS) — retained as the invariant to restore alongside the
  // read/unread treatment that re-surfaces external todos.
  if (a.dimension === 'in_flow') return 0;
  const ta = a.latest_at !== null ? Date.parse(a.latest_at) : Number.NaN;
  const tb = b.latest_at !== null ? Date.parse(b.latest_at) : Number.NaN;
  if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
  // Newest-first for every band (open_task included as of 2026-07-30).
  return tb - ta;
}

/**
 * Flatten the pills of every ContactStateListRow into one flat, ranked Todo[]
 * (plan U2). Pure — no I/O. Excludes `recent_request` and `gifted` pills
 * (no valid dest). Sorts by pillPriorityRank; ties broken by latest_at per
 * `compareLatestAt`, then by stable insertion order (row order, then pill
 * order within a row). `rank` is the 1-based ordinal in the returned list.
 */
export function rankTodos(rows: ContactStateListRow[]): Todo[] {
  type Candidate = {
    contact: ContactHeader;
    pill: ContactStatePill;
    dest: TodoDest;
    priority: number;
    order: number;
  };
  const candidates: Candidate[] = [];
  let order = 0;
  for (const r of rows) {
    for (const pill of r.pills) {
      const dest = todoDestForDimension(pill.dimension);
      if (dest === null) continue;
      // Temporarily hide EXTERNAL-completion todos (Spencer, 2026-07-28): a
      // `gong` (flag-only Gong Flow) or `launcher` (campaign-member invite)
      // todo is completed in a third-party system (Gong / Playhouse), which
      // gives Terra no completion signal and only a lagging mirror — so the
      // sidebar can't honestly drop them on completion the way it can for
      // Action-View tasks (awaiting_sync overlay) and queued plays
      // (Terra-native). Until the read/unread ("opened → demote") treatment
      // for external todos lands, they're excluded from the ranked list
      // rather than nagging through the sync gap. Restore point tracked in
      // memory (project_sidebar_redesign: read/unread for external todos).
      if (HIDDEN_TODO_DESTS.has(dest)) continue;
      candidates.push({
        contact: r.contact,
        pill,
        dest,
        priority: pillPriorityRank(pill),
        order,
      });
      order += 1;
    }
  }

  candidates.sort((a, b) => {
    // Cross-dimension PILL_DIMENSION_PRIORITY is ALWAYS the top key (Queued
    // first, etc.) — never displaced (ranking-consistency invariant, 2026-07-30).
    if (a.priority !== b.priority) return a.priority - b.priority;
    // WITHIN a band, inherited admin-PriorityConfig rank breaks the tie next
    // (open_task pills only, today). Absent rank → +Infinity, so a NO-OP when
    // both sides lack it (Infinity !== Infinity is false) — Queued-first and
    // the full cross-dimension order are preserved for every pre-feature pill.
    const ar = a.pill.priorityRank ?? Number.POSITIVE_INFINITY;
    const br = b.pill.priorityRank ?? Number.POSITIVE_INFINITY;
    if (ar !== br) return ar - br;
    const byRecency = compareLatestAt(a.pill, b.pill);
    if (byRecency !== 0) return byRecency;
    return a.order - b.order;
  });

  return candidates.map((c, i) => ({
    contact: c.contact,
    pill: c.pill,
    rank: i + 1,
    evidence_ref: null,
    dest: c.dest,
  }));
}
